import { useEffect, useRef } from 'react'
import { useAuth } from 'react-oidc-context'
import { useCurrentLocale } from '@/locales/client'
import { useLogin } from '@/features/authentication'
import { getEnv } from '@/lib/config/runtime-env'

// The global command function exposed by the hosted bootstrap. Before the real
// bundle loads it is a queue stub that buffers calls on `.q`; the bundle then
// replays them. Same call signature either way, so callers never wait.
type AmeenWidgetGlobal = ((command: string, arg?: unknown) => void) & {
  q?: unknown[]
}

declare global {
  interface Window {
    AmeenWidget?: AmeenWidgetGlobal
  }
}

// Install the command queue and inject the hosted bootstrap script once.
// Idempotent: guards on both the global and the injected <script> tag, so React
// re-renders / fast-refresh never duplicate either.
function ensureAmeenScript(scriptSrc: string): AmeenWidgetGlobal {
  if (!window.AmeenWidget) {
    const queue = function (...args: unknown[]) {
      queue.q = queue.q || []
      queue.q.push(args)
    } as AmeenWidgetGlobal
    window.AmeenWidget = queue
  }

  if (!document.querySelector('script[data-ameen-embed]')) {
    const script = document.createElement('script')
    script.async = true
    script.src = scriptSrc
    script.dataset.ameenEmbed = '1'
    document.head.appendChild(script)
  }

  return window.AmeenWidget
}

// Derive the hosted-bootstrap URL from the Ameen base URL (VITE_AMEEN_URL is the
// base, e.g. https://www.tameeni.com/ameen).
function toBootstrapUrl(base: string): string {
  // Trim trailing slashes with a linear scan instead of a regex, so a base URL
  // ending in many slashes can't trigger regex backtracking (ReDoS).
  let end = base.length
  while (end > 0 && base.charCodeAt(end - 1) === 47 /* '/' */) end--
  return `${base.slice(0, end)}/api/widget/v1.js`
}

// MMP's Ameen application reference (fixed per product, not environment-specific).
const AMEEN_APPLICATION_REFERENCE = 'AE6814F6-8593-4553-3F6C-08DA0A78C949'

function ChatWidgetClient() {
  const locale = useCurrentLocale()
  const uiLocale = locale === 'en' ? 'en' : 'ar'
  const { user, isLoading, isAuthenticated } = useAuth()
  const { signIn } = useLogin()
  const { AMEEN_URL } = getEnv()
  const accessToken =
    isAuthenticated && user?.access_token ? user.access_token : undefined

  // Has `init` run yet? Gates the update effects so they don't fire pre-boot.
  const bootedRef = useRef(false)
  // Last token handed to the widget, so we skip redundant `updateToken` calls.
  const appliedTokenRef = useRef<string | null | undefined>(undefined)
  // Latest `signIn`, so the `onAuthRequired` closure captured once at `init`
  // always delegates to the current login trigger instead of a stale one.
  const signInRef = useRef(signIn)
  signInRef.current = signIn

  // Don't show the widget when embedded as an MFE inside another app's iframe.
  const isEmbedded = typeof window !== 'undefined' && window.self !== window.top

  // Boot once, after the session settles (so we init with a resolved token
  // rather than the initial `undefined`).
  useEffect(() => {
    if (isEmbedded || bootedRef.current) return
    if (isLoading || !AMEEN_URL) return

    const initOptions = {
      applicationReference: AMEEN_APPLICATION_REFERENCE,
      locale: uiLocale,
      // The widget calls this when it needs a token (unauthenticated or expired).
      // Trigger the app's login flow, returning to the current page afterwards;
      // the resolved token is pushed back into the live widget by the
      // `updateToken` effect below once `accessToken` changes.
      onAuthRequired: () =>
        signInRef.current({ postLoginRedirectionUrl: window.location.href }),
      ...(accessToken ? { accessToken } : {}),
    }

    ensureAmeenScript(toBootstrapUrl(AMEEN_URL))('init', initOptions)

    bootedRef.current = true
    appliedTokenRef.current = accessToken
  }, [isEmbedded, isLoading, accessToken, uiLocale, AMEEN_URL])

  // Push token rotations (sign-in / sign-out / refresh) into the live widget.
  useEffect(() => {
    if (!bootedRef.current || appliedTokenRef.current === accessToken) return
    appliedTokenRef.current = accessToken
    window.AmeenWidget?.('updateToken', accessToken ?? null)
  }, [accessToken])

  // Push locale flips into the live widget.
  useEffect(() => {
    if (!bootedRef.current) return
    window.AmeenWidget?.('setLocale', uiLocale)
  }, [uiLocale])

  // The widget renders its own floating launcher and manages its own DOM.
  return null
}

export default ChatWidgetClient
